// What the room collectively missed on a step.
//
// For every expert item on a step (the case's findings on History, Physical
// and Labs; the expert differential's diseases; the expert CDM answers) this
// counts how many students listed something that matches it, and therefore how
// many did not. The facilitator's client computes it at Merge & share or Reveal
// and writes it to caseFlow/classGaps/$scope, so everyone reads one answer and
// the export and archive carry it.
//
// Matching mirrors the personal gaps: the permissive token tier on the boards
// (what the reveal itself lights up) and strict matching on diagnoses, where
// "pulmonary edema" must never count as "pulmonary embolism".

import { fuzzyMatch } from './matching';
import { studentSaid } from './mergeMatch';

export const BOARD_SCOPES = ['history', 'physical', 'labs'];

export const isBoardScope = (scope) => BOARD_SCOPES.includes(scope);

/** The expert items for a scope, each as { text, aliases?, detail? }. */
export function expertItems(caseFlow, scope) {
  if (!caseFlow?.active) return [];
  if (scope === 'differential') {
    // The expert differential's diseases carry no aliases of their own, so
    // each borrows the aliases of the expert CDM answer it names, if any:
    // "ATN" then credits "Ischemic acute tubular necrosis".
    const cdm = caseFlow.cdmAnswer || [];
    return (caseFlow.diffAnswer || []).flatMap((e) => (e.diseases || []).map((d) => {
      const twin = cdm.find((c) => fuzzyMatch(d, c) || fuzzyMatch(c.text, { text: d }));
      return { text: d, aliases: twin?.aliases || [] };
    }));
  }
  if (scope === 'cdm') {
    return (caseFlow.cdmAnswer || []).map((e) => ({ text: e.text, aliases: e.aliases || [] }));
  }
  return (caseFlow.findings?.[scope] || []).map((f) => ({ text: f.text, aliases: f.aliases || [], detail: f.detail || '' }));
}

// The reveal's matching plus a typo's grace (utils/mergeMatch.js), so the
// count agrees with what the merge collapsed into one card.
const matcher = (scope) => (isBoardScope(scope)
  ? (text, item) => studentSaid(text, item, { tokens: true })
  : (text, item) => studentSaid(text, item));

/**
 * textsByStudent: Map<studentKey, string[]> of what each student entered on
 * the step. total: how many students count as "the room" (present plus
 * anyone who contributed). Returns the items sorted worst-missed first.
 */
export function computeClassGaps(caseFlow, scope, textsByStudent, total) {
  const match = matcher(scope);
  const items = expertItems(caseFlow, scope).map((item) => {
    let listed = 0;
    textsByStudent.forEach((texts) => {
      if ((texts || []).some((t) => match(t, item))) listed += 1;
    });
    return { text: item.text, detail: item.detail || '', listed, missed: Math.max(0, total - listed) };
  });
  items.sort((a, b) => b.missed - a.missed || a.text.localeCompare(b.text));
  return { total, computedAt: Date.now(), items };
}

/**
 * Per-student texts from the SHARED items of a step (group mode). Entries the
 * case or a merge added ("Case", "Class") and the facilitator's own are not
 * students' work and are left out.
 */
export function textsFromSharedItems(itemNodes, { facilitatorKey } = {}) {
  const out = new Map();
  (itemNodes || []).forEach((node) => {
    Object.values(node || {}).forEach((item) => {
      const key = item?.addedByKey;
      if (!key || key === facilitatorKey || item.addedBy === 'Case' || item.addedBy === 'Class') return;
      if (!out.has(key)) out.set(key, []);
      out.get(key).push(item.text || '');
    });
  });
  return out;
}

/** Identity keys of everyone present who is not the facilitator. */
export function presentStudentKeys(presence, facilitatorKey) {
  return new Set(
    Object.values(presence || {}).map((p) => p?.key).filter((k) => k && k !== facilitatorKey),
  );
}

/** "Missed by 9 of 12" */
export const missedLabel = (item, total) => `Missed by ${item.missed} of ${total}`;
